import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/react-hooks';
import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Text,
  Textarea,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';

import {
  UPDATE_SPIRIT_POINT_TEMPLATE,
  DELETE_SPIRIT_POINT_TEMPLATE,
} from '../../../GraphQL/Mutations';

const getBorderColor = (amount) => (amount >= 0 ? '#53A748' : '#dc3545');

export const TemplateRow = ({
  template, onUpdate, onRemove, onError,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(template.title);
  const [amount, setAmount] = useState(template.amount);
  const [description, setDescription] = useState(template.description || '');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [updateTemplate, { loading: updateLoading }] = useMutation(
    UPDATE_SPIRIT_POINT_TEMPLATE,
    {
      onCompleted: (data) => {
        onUpdate(data.updateSpiritPointTemplate.template);
        setIsEditing(false);
      },
      onError: (err) => {
        onError(err.graphQLErrors?.[0]?.message ?? err.message);
      },
    },
  );

  const [deleteTemplate, { loading: deleteLoading }] = useMutation(
    DELETE_SPIRIT_POINT_TEMPLATE,
    {
      onCompleted: () => {
        onRemove(template.id);
      },
      onError: (err) => {
        onError(err.graphQLErrors?.[0]?.message ?? err.message);
      },
    },
  );

  const handleSave = () => {
    if (!title.trim()) {
      onError('Title is required');
      return;
    }
    if (amount === '' || amount === null || amount === undefined) {
      onError('Points value is required');
      return;
    }

    updateTemplate({
      variables: {
        input: {
          id: template.id,
          title: title.trim(),
          amount: parseInt(amount, 10),
          description: description.trim() || title.trim(),
        },
      },
    });
  };

  const handleCancel = () => {
    setTitle(template.title);
    setAmount(template.amount);
    setDescription(template.description || '');
    setIsEditing(false);
  };

  const confirmDelete = () => {
    deleteTemplate({
      variables: {
        input: {
          id: template.id,
        },
      },
    });
    onClose();
  };

  const loading = updateLoading || deleteLoading;
  const isPositive = template.amount >= 0;
  const pointsDisplay = isPositive ? `+${template.amount}` : template.amount;
  const isFallback = parseInt(template.id, 10) <= 0;

  if (isEditing) {
    return (
      <Box
        borderLeftWidth="8px"
        borderLeftColor="#008DC5"
        borderRadius="8"
        background="#f2f2f2"
        padding="16px"
      >
        <VStack align="stretch" spacing={4}>
          <Flex alignItems="flex-start">
            <Box flex="1">
              <Text mb={1}>
                Title
                <Text as="span" color="red.500"> *</Text>
              </Text>
              <Input bg="white" value={title} onChange={(e) => setTitle(e.target.value)} />
            </Box>
            <Button variant="link" onClick={handleCancel} isDisabled={loading}>Cancel</Button>
          </Flex>
          <Box>
            <Text mb={1}>
              Points
              <Text as="span" color="red.500"> *</Text>
            </Text>
            <NumberInput value={amount} onChange={(val) => setAmount(val)} maxW="150px">
              <NumberInputField bg="white" />
            </NumberInput>
          </Box>
          <Box>
            <Text mb={1}>Description</Text>
            <Textarea
              bg="white"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="(optional)"
              rows={2}
            />
          </Box>
          <HStack justifyContent="flex-end">
            <Button
              colorScheme="orange"
              onClick={handleSave}
              isLoading={updateLoading}
              isDisabled={!title.trim() || amount === '' || amount === null}
            >
              Save
            </Button>
          </HStack>
        </VStack>
      </Box>
    );
  }

  return (
    <Box
      borderLeftWidth="8px"
      borderLeftColor={getBorderColor(template.amount)}
      borderRadius="8"
      background="#f2f2f2"
      padding="16px"
    >
      <Flex justifyContent="space-between" alignItems="flex-start">
        <Box>
          <HStack spacing={3}>
            <Text fontWeight="bold">{template.title}</Text>
            <Text color={isPositive ? 'green.600' : 'red.600'} fontWeight="semibold">
              {pointsDisplay} {Math.abs(template.amount) === 1 ? 'point' : 'points'}
            </Text>
          </HStack>
          {template.description && (
            <Text fontSize="sm" color="gray.600" mt={1}>
              {template.description}
            </Text>
          )}
        </Box>
        {isFallback ? (
          <Text fontSize="sm" color="gray.500" fontStyle="italic">default</Text>
        ) : (
          <HStack spacing={0}>
            <Button variant="link" onClick={() => setIsEditing(true)}>edit</Button>
            <Text color="gray.400" px={1}>|</Text>
            <Button variant="link" onClick={onOpen} isLoading={deleteLoading}>delete</Button>
          </HStack>
        )}
      </Flex>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Template</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>Are you sure you want to delete &quot;{template.title}&quot;?</Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="red" mr={3} onClick={confirmDelete} isLoading={deleteLoading}>
              Delete
            </Button>
            <Button colorScheme="gray" variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

TemplateRow.propTypes = {
  template: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    amount: PropTypes.number.isRequired,
    description: PropTypes.string,
  }).isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};
